'use client';

import React from 'react';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetSystemVolumeQuery } from 'src/api/redux';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import {
  applyDisplayMode,
  applyCaptureRate,
} from 'src/components/Profiles/shared/widgets/utils';
import { useResolveSystemId } from './useResolveSystemId';

const MAX_PREVIEW_ROWS = 3;

const SystemVolumePreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;

  const codeSetIds = [
    ...(widget?.code_sets ?? []),
    ...(widget?.code_set ? [widget.code_set] : []),
  ].map((cs: { id: number }) => cs.id);

  const encounterIds = (widget?.encounters ?? []).map(
    (enc: { id: number }) => enc.id
  );

  const systemId = useResolveSystemId(
    entityDetails,
    widget?.network_rollup === true
  );

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;

  const hasSources = codeSetIds.length > 0 || encounterIds.length > 0;

  const { data, isLoading: queryLoading } = useGetSystemVolumeQuery(
    {
      systemId: systemId || '',
      codeSetIds: codeSetIds.length > 0 ? codeSetIds : undefined,
      encounterIds: encounterIds.length > 0 ? encounterIds : undefined,
      minYear: minYear ?? undefined,
      maxYear: maxYear ?? undefined,
      filterSetId: widget?.filter_set?.id ?? undefined,
    },
    { skip: !systemId || !hasSources }
  );

  const displayMode = widget?.display_mode;
  const captureRate = widget?.capture_rate;
  const volumeType = widget?.volume_type ?? 'claims';

  const formatValue = (value: number) =>
    handleNumericValue(
      applyCaptureRate(applyDisplayMode(value, displayMode), captureRate)
    );

  const parentSummary = widgetConfig?.parentSummary;

  const summaryBlock = parentSummary ? (
    <p
      className="text-xs text-neutral-500 line-clamp-2"
      data-testid="system-volume-preview-summary"
    >
      {parentSummary}
    </p>
  ) : null;

  if (queryLoading || widgetConfig?.isLoading) {
    return (
      <div
        className="flex flex-col gap-2"
        data-testid="system-volume-preview-loading"
      >
        {summaryBlock}
        <WidgetLoading aria-label="Loading system volume" />
      </div>
    );
  }

  const allRows = [
    ...(data?.code_sets ?? []).map((cs) => ({
      key: `cs-${cs.code_set?.id ?? 0}`,
      label: cs.code_set?.label ?? 'Unknown',
      claims: Number(cs.claims),
      patients: Number(cs.patients),
    })),
    ...(data?.encounters ?? []).map((enc) => ({
      key: `enc-${enc.encounter?.id ?? 0}`,
      label: enc.encounter?.label ?? 'Unknown',
      claims: Number(enc.claims),
      patients: Number(enc.patients),
    })),
  ];

  if (allRows.length === 0) {
    return (
      <div
        className="flex flex-col gap-2"
        data-testid="system-volume-preview-empty"
      >
        {summaryBlock}
        <WidgetEmpty />
      </div>
    );
  }

  // Single source → hero layout (established pattern)
  if (allRows.length === 1) {
    const row = allRows[0];
    const showPatientsAsHero = volumeType === 'patients';
    const heroRawValue = showPatientsAsHero ? row.patients : row.claims;
    const heroValue = formatValue(heroRawValue);
    const heroLabel = showPatientsAsHero ? 'Patients' : 'Claims';

    const hasSubtitle = !widget?.volume_type;
    const subtitleRawValue = showPatientsAsHero ? row.claims : row.patients;
    const subtitleValue = formatValue(subtitleRawValue);
    const subtitleLabel = showPatientsAsHero ? 'Claims' : 'Patients';

    return (
      <div
        className="flex flex-col gap-2"
        data-testid="system-volume-preview"
      >
        {summaryBlock}
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-3">
            <span
              className="text-4xl font-bold tabular-nums text-sky-600"
              data-testid="system-volume-preview-hero-value"
            >
              {heroValue}
            </span>
            <span
              className="text-sm font-semibold text-neutral-500"
              data-testid="system-volume-preview-hero-label"
            >
              {heroLabel}
            </span>
          </div>
          {hasSubtitle && (
            <p
              className="text-sm text-neutral-500"
              data-testid="system-volume-preview-subtitle"
            >
              {subtitleValue} {subtitleLabel}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Multiple sources → list of up to 3 rows
  const rows = allRows.slice(0, MAX_PREVIEW_ROWS);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="system-volume-preview"
    >
      {summaryBlock}
      <div
        className="flex flex-col"
        role="list"
        aria-label="System volume sources"
      >
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-b-0"
            role="listitem"
            data-testid={`system-volume-preview-row-${row.key}`}
          >
            <span className="text-sm font-medium text-primary truncate">
              {row.label}
            </span>
            <span className="text-sm font-semibold text-teal-900 tabular-nums flex-shrink-0 ml-4">
              {formatValue(volumeType === 'patients' ? row.patients : row.claims)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemVolumePreview;
